import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileText, Plus, RefreshCw, RotateCcw, Save, Search, Settings, Trash2, Upload, X } from 'lucide-react';
import { apiClient } from '../../api/client';
import type {
  AutoFillKind,
  ContractGenerationSummary,
  ContractPlaceholder,
  ContractTemplateDetail,
  ContractTemplateSummary,
  GenerateContractResponse,
  PlaceholderKind,
} from '../../types/contracts';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import './ContractGeneratorModal.css';

interface ContractGeneratorModalProps {
  open: boolean;
  onClose: () => void;
}

type StoredValuesMap = Record<string, Record<string, string>>;
type ContractOutputFormat = 'pdf' | 'docx';
type ActiveTab = 'create' | 'templates';

const STORAGE_KEY = 'contract-generator-last-values-v1';

const loadStoredValues = (): StoredValuesMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const saveStoredValues = (data: StoredValuesMap) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const getTemplateDefaultName = (fileName: string) => fileName.replace(/\.(docx|pdf)$/i, '').trim();

const createEmptyValues = (placeholders: ContractPlaceholder[]) =>
  placeholders.reduce<Record<string, string>>((acc, item) => {
    acc[item.key] = '';
    return acc;
  }, {});

const getFieldElementId = (key: string) =>
  `contract-field-${key.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/-+/g, '-')}`;

const getFormatLabel = (format: 'docx' | 'pdf') => format.toUpperCase();

const getFileFormat = (file: File | null): 'docx' | 'pdf' | null => {
  if (!file) return null;
  return file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
};

const toTemplateSummary = (template: ContractTemplateDetail): ContractTemplateSummary => ({
  id: template.id,
  name: template.name,
  original_filename: template.original_filename,
  placeholder_prefix: template.placeholder_prefix,
  placeholder_syntax: template.placeholder_syntax,
  source_format: template.source_format,
  placeholders_count: template.placeholders.length,
  next_number: template.next_number,
  number_placeholder_key: template.number_placeholder_key,
  client_slug_key: template.client_slug_key,
  created_count: template.created_count,
  last_number: template.last_number,
  updated_at: template.updated_at,
});

const downloadBlobResult = (result: GenerateContractResponse) => {
  const objectUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = result.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

/**
 * Достаёт из axios-ошибки короткое человеческое сообщение.
 * Поддерживает форматы FastAPI:
 *   - detail как строка ("...")
 *   - detail как объект {message, missing_keys?, remaining_keys?}
 *   - detail как массив Pydantic-валидации [{loc, msg, type, input}, ...]
 *   - иначе — err.message или fallback.
 * НИЧЕГО не логирует — только парсит.
 */
function formatApiError(err: any, fallback: string): { message: string; detail?: unknown } {
  const responseData = err?.response?.data;
  const detail = responseData?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return { message: detail, detail };
  }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const message = (detail as any).message;
    if (typeof message === 'string' && message.trim()) {
      return { message, detail };
    }
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail
      .map((item: any) => {
        const loc = Array.isArray(item?.loc) ? item.loc.filter(Boolean).join('.') : '';
        const msg = typeof item?.msg === 'string' ? item.msg : '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length > 0) {
      return { message: `Ошибка валидации: ${parts.join('; ')}`, detail };
    }
  }
  if (typeof responseData === 'string' && responseData.trim()) {
    return { message: responseData, detail: responseData };
  }
  if (typeof err?.message === 'string' && err.message.trim()) {
    return { message: err.message, detail: responseData };
  }
  return { message: fallback, detail: responseData };
}

/**
 * Логирует ошибку API в console.error с полным контекстом (scope, status, url, body, message).
 * Возвращает короткое сообщение для UI — то же, что formatApiError.
 */
function logApiError(scope: string, err: any, fallback: string): string {
  const { message, detail } = formatApiError(err, fallback);
  const status = err?.response?.status;
  const url = err?.config?.url;
  const method = err?.config?.method?.toUpperCase?.();
  const fullUrl = url ? `${(err.config as any)?.baseURL ?? ''}${url}` : undefined;
  // eslint-disable-next-line no-console
  console.error(
    `[${scope}] ${status ?? '???'} ${method ?? ''} ${url ?? ''}`.trimEnd(),
    {
      message,
      status,
      statusText: err?.response?.statusText,
      url: fullUrl,
      detail,
      responseData: err?.response?.data,
      axiosMessage: err?.message,
    },
  );
  return message;
}

const formatDateRu = (iso: string) => new Date(iso).toLocaleDateString('ru-RU');

const getClientDataEntries = (generation: ContractGenerationSummary): Array<[string, string]> =>
  Object.entries(generation.client_data || {})
    .map(([key, value]) => [key, String(value || '').trim()] as [string, string])
    .filter(([, value]) => Boolean(value))
    .slice(0, 3);

// Эвристика авто-подстановки по label плейсхолдера — работает автоматически
// для формы создания договора (без UI-контролов и без участия админа).
const AUTO_FILL_BY_LABEL: Array<{ autoFill: AutoFillKind; matchers: RegExp[] }> = [
  { autoFill: 'today_day', matchers: [/^\s*(число|день|day|какое\s+число|число\s+месяца)\s*$/i] },
  { autoFill: 'today_month', matchers: [/^\s*(месяц|month)\s*$/i] },
  { autoFill: 'today_year', matchers: [/^\s*(год|year)\s*$/i] },
  { autoFill: 'contract_number', matchers: [/№\s*договора|номер\s+договора|contract[\s_-]?number/i] },
];

function detectAutoFillByLabel(label: string): AutoFillKind | null {
  const text = (label || '').trim();
  if (!text) return null;
  for (const { autoFill, matchers } of AUTO_FILL_BY_LABEL) {
    if (matchers.some((regex) => regex.test(text))) return autoFill;
  }
  return null;
}

/**
 * Собирает карту auto_fill для шаблона. Приоритет:
 *   1) явный placeholder.auto_fill (если бэкенд/админ когда-то задал),
 *   2) эвристика по label (число/месяц/год/№ договора),
 *   3) запись для number_placeholder_key → 'contract_number'.
 */
function buildAutoFillMap(
  template: ContractTemplateDetail,
): Record<string, AutoFillKind> {
  const map: Record<string, AutoFillKind> = {};
  const numberKey = template.number_placeholder_key || 'contract_number';
  map[numberKey] = 'contract_number';
  for (const item of template.placeholders) {
    if (item.auto_fill) {
      map[item.key] = item.auto_fill;
      continue;
    }
    const byLabel = detectAutoFillByLabel(item.label || item.key || '');
    if (byLabel) map[item.key] = byLabel;
  }
  return map;
}

interface TodayParts {
  day: string;
  month: string;
  year: string;
  fullYear: string;
}

const computeTodayParts = (): TodayParts => {
  const now = new Date();
  const monthGenitive = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ][now.getMonth()];
  return {
    day: String(now.getDate()).padStart(2, '0'),
    month: monthGenitive,
    year: String(now.getFullYear()).slice(-1),
    fullYear: String(now.getFullYear()),
  };
};

const normalizeSelectOptions = (options: string[] | null | undefined): string[] =>
  (options || []).map((option) => option.trim()).filter(Boolean);

const pickDefaultClientSlugKey = (placeholders: ContractPlaceholder[]): string => {
  const byKey = placeholders.find((item) => item.key === 'customer_name');
  if (byKey) return byKey.key;
  const byLabel = placeholders.find((item) =>
    /данные\s+клиента|клиент|заказчик/i.test(`${item.label || ''} ${item.key || ''}`),
  );
  return byLabel?.key || placeholders[0]?.key || 'customer_name';
};

type DateAutoFillKind = Exclude<AutoFillKind, 'contract_number'>;

const fillAutoKeys: Record<DateAutoFillKind, keyof TodayParts> = {
  today_day: 'day',
  today_month: 'month',
  today_year: 'year',
};

const getPlaceholderHint = (
  placeholder: ContractPlaceholder,
  autoFillMap: Record<string, AutoFillKind>,
  todayParts: TodayParts,
): string => {
  if (autoFillMap[placeholder.key] === 'today_year') {
    const yearPrefix = todayParts.fullYear.slice(0, -1);
    return `Нужно ввести только последнюю цифру года: базово подставится ${todayParts.year}, в документе будет ${yearPrefix}${todayParts.year}`;
  }
  return placeholder.hint?.trim() || '';
};

/**
 * Применяет автоподстановки (today_day/month/year + contract_number) к values.
 * Не трогает ключи, которые пользователь уже редактировал вручную (touched).
 */
function applyAutoFill(
  values: Record<string, string>,
  autoFillMap: Record<string, AutoFillKind>,
  autoTouchedKeys: Record<string, true>,
  contractNumber: string | null,
  todayParts: TodayParts,
): Record<string, string> {
  const out: Record<string, string> = { ...values };
  for (const [key, kind] of Object.entries(autoFillMap)) {
    if (autoTouchedKeys[key]) continue;
    if (kind === 'contract_number') {
      if (contractNumber) out[key] = contractNumber;
      continue;
    }
    out[key] = todayParts[fillAutoKeys[kind as DateAutoFillKind]];
  }
  return out;
}

const ContractGeneratorModal: React.FC<ContractGeneratorModalProps> = ({ open, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceFileTemplateIdRef = useRef<number | null>(null);
  const storedValuesRef = useRef<StoredValuesMap>({});
  const selectedTemplateIdRef = useRef<number | null>(null);
  const previewOptionsTextareaRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const settingsOptionsTextareaRefs = useRef<Array<HTMLTextAreaElement | null>>([]);

  const [templates, setTemplates] = useState<ContractTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplateDetail | null>(null);

  // Состояние формы редактирования шаблона (для вкладки «Шаблоны»)
  const [templateName, setTemplateName] = useState('');
  const [templatePrefix, setTemplatePrefix] = useState('##');
  const [templateNextNumber, setTemplateNextNumber] = useState('1');
  const [templateNumberPlaceholderKey, setTemplateNumberPlaceholderKey] = useState('contract_number');
  const [templateClientSlugKey, setTemplateClientSlugKey] = useState('customer_name');

  // Поля контракта (для вкладки «Создать договор»)
  const [values, setValues] = useState<Record<string, string>>({});
  const [validationKeys, setValidationKeys] = useState<string[]>([]);

  // Состояние аплоада нового шаблона
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadNextNumber, setUploadNextNumber] = useState('1');
  const [uploadNumberPlaceholderKey, setUploadNumberPlaceholderKey] = useState('номер_договора');
  const [uploadClientSlugKey, setUploadClientSlugKey] = useState('customer_name');
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);

  // Превью распознанных placeholder'ов: { placeholder → auto_fill? } для редактирования перед сохранением
  const [previewPlaceholders, setPreviewPlaceholders] = useState<ContractPlaceholder[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequestRef = useRef(0);

  // Замена файла
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceFileTemplateId, setReplaceFileTemplateId] = useState<number | null>(null);

  // Формат вывода
  const [outputFormat, setOutputFormat] = useState<ContractOutputFormat>('pdf');

  // Кэш последних значений
  const [storedValues, setStoredValues] = useState<StoredValuesMap>({});

  // История генераций
  const [generations, setGenerations] = useState<ContractGenerationSummary[]>([]);

  // UI / loading
  const [activeTab, setActiveTab] = useState<ActiveTab>('create');
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generationsLoading, setGenerationsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [replacingTemplate, setReplacingTemplate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const [regeneratingGenerationId, setRegeneratingGenerationId] = useState<number | null>(null);
  const [loadingGenerationId, setLoadingGenerationId] = useState<number | null>(null);
  const [viewingGeneration, setViewingGeneration] = useState<ContractGenerationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ─── Auto-fill state ─────────────────────────────────────
  // Фиксируется один раз при открытии модала, чтобы «07» не дрейфовал во время набора.
  const [todayParts] = useState<TodayParts>(computeTodayParts);
  // Ключи плейсхолдеров, которые пользователь трогал руками — auto-fill для них выключен до сброса.
  const [autoTouchedKeys, setAutoTouchedKeys] = useState<Record<string, true>>({});
  // Локальная копия плейсхолдеров для inline-редактирования в «Настройки» шаблона.
  const [placeholderEdits, setPlaceholderEdits] = useState<ContractPlaceholder[]>([]);
  const [placeholdersSaving, setPlaceholdersSaving] = useState(false);

  const uploadSourceFormat = useMemo(() => getFileFormat(uploadFile), [uploadFile]);
  const replaceSourceFormat = useMemo(() => getFileFormat(replaceFile), [replaceFile]);

  const lastValuesForSelectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return storedValues[String(selectedTemplateId)] || null;
  }, [selectedTemplateId, storedValues]);

  const fillablePlaceholders = useMemo(() => {
    if (!selectedTemplate) return [];
    const numberKey = selectedTemplate.number_placeholder_key || 'contract_number';
    return selectedTemplate.placeholders.filter((item) => item.key !== numberKey);
  }, [selectedTemplate]);

  const fillableRequiredCount = useMemo(
    () => fillablePlaceholders.filter((item) => item.required).length,
    [fillablePlaceholders],
  );

  const filledRequiredCount = useMemo(() => {
    if (!selectedTemplate) return 0;
    return fillablePlaceholders.filter(
      (item) => item.required && String(values[item.key] || '').trim(),
    ).length;
  }, [values, fillablePlaceholders, selectedTemplate]);

  const numberPlaceholderMissing = useMemo(() => {
    if (!selectedTemplate?.number_placeholder_key) return false;
    return !selectedTemplate.placeholders.some((item) => item.key === selectedTemplate.number_placeholder_key);
  }, [selectedTemplate]);

  // Карта auto_fill: ключ placeholder'а → что подставлять.
  // Приоритет: явный placeholder.auto_fill → авто-эвристика по label → null.
  // Номер договора всегда резолвится через number_placeholder_key.
  const autoFillMap = useMemo<Record<string, AutoFillKind>>(() => {
    return selectedTemplate ? buildAutoFillMap(selectedTemplate) : {};
  }, [selectedTemplate]);

  useEffect(() => {
    storedValuesRef.current = storedValues;
  }, [storedValues]);

  useEffect(() => {
    setViewingGeneration(null);
  }, [selectedTemplateId]);

  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId;
  }, [selectedTemplateId]);

  const loadGenerations = useCallback(async (templateId: number) => {
    setGenerationsLoading(true);
    try {
      const list = await apiClient.listContractGenerations(templateId);
      setGenerations(list);
    } catch (err: any) {
      setError(logApiError('contracts:load-generations', err, 'Не удалось загрузить историю договоров.'));
    } finally {
      setGenerationsLoading(false);
    }
  }, []);

  const loadTemplateDetail = useCallback(async (templateId: number, cacheMap?: StoredValuesMap) => {
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await apiClient.getContractTemplate(templateId);
      setSelectedTemplate(detail);
      setTemplateName(detail.name);
      setTemplatePrefix(detail.placeholder_prefix || '');
      setTemplateNextNumber(String(detail.next_number || 1));
      setTemplateNumberPlaceholderKey(detail.number_placeholder_key || 'contract_number');
      setTemplateClientSlugKey(detail.client_slug_key || 'customer_name');
      setOutputFormat('pdf');
      setReplaceFile(null);
      setReplaceFileTemplateId(null);
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = '';
      }
      const effectiveCache = cacheMap ?? storedValuesRef.current;
      const cachedValues = effectiveCache[String(templateId)] || {};
      const contractNumberValue = String(detail.next_number || 1);
      const baseValues: Record<string, string> = {
        ...createEmptyValues(detail.placeholders),
        ...cachedValues,
      };
      const nextMap = buildAutoFillMap(detail);
      setValues(
        applyAutoFill(baseValues, nextMap, {}, contractNumberValue, todayParts),
      );
      setAutoTouchedKeys({});
      setValidationKeys([]);
      setViewingGeneration(null);
      await loadGenerations(templateId);
    } catch (err: any) {
      setError(logApiError('contracts:load-template', err, 'Не удалось загрузить шаблон.'));
    } finally {
      setDetailLoading(false);
    }
  }, [loadGenerations, todayParts]);

  const loadTemplates = useCallback(async (preferredTemplateId?: number, cacheMap?: StoredValuesMap) => {
    setListLoading(true);
    setError(null);
    try {
      const list = await apiClient.listContractTemplates();
      setTemplates(list);
      const currentSelectedTemplateId = selectedTemplateIdRef.current;
      const nextSelectedId =
        preferredTemplateId && list.some((item) => item.id === preferredTemplateId)
          ? preferredTemplateId
          : currentSelectedTemplateId && list.some((item) => item.id === currentSelectedTemplateId)
            ? currentSelectedTemplateId
            : list[0]?.id ?? null;
      setSelectedTemplateId(nextSelectedId);
      if (nextSelectedId) {
        await loadTemplateDetail(nextSelectedId, cacheMap);
      } else {
        setSelectedTemplate(null);
        setTemplateName('');
        setTemplatePrefix('');
        setTemplateNextNumber('1');
        setTemplateNumberPlaceholderKey('contract_number');
        setTemplateClientSlugKey('customer_name');
        setValues({});
        setGenerations([]);
      }
    } catch (err: any) {
      setError(logApiError('contracts:load-templates', err, 'Не удалось загрузить шаблоны.'));
    } finally {
      setListLoading(false);
    }
  }, [loadTemplateDetail]);

  useEffect(() => {
    if (!open) return;
    const initialStoredValues = loadStoredValues();
    setStoredValues(initialStoredValues);
    setActiveTab('create');
    setEditingTemplateId(null);
    setUploadPanelOpen(false);
    void loadTemplates(undefined, initialStoredValues);
  }, [loadTemplates, open]);

  useEffect(() => {
    if (!open || !selectedTemplateId) return;
    if (selectedTemplate?.id === selectedTemplateId) return;
    void loadTemplateDetail(selectedTemplateId);
  }, [loadTemplateDetail, open, selectedTemplate, selectedTemplateId]);

  const handleUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setUploadFile(file);
    setPreviewPlaceholders(null);
    setPreviewError(null);
    setSuccessMessage(null);
    if (file) {
      setUploadName(getTemplateDefaultName(file.name));
      // Запускаем превью с задержкой, чтобы не дёргать API на каждый клик
      const requestId = previewRequestRef.current + 1;
      previewRequestRef.current = requestId;
      setPreviewLoading(true);
      const timer = window.setTimeout(() => {
        void (async () => {
          try {
            const result = await apiClient.previewContractTemplate({ file });
            if (previewRequestRef.current !== requestId) return; // устаревший ответ
            setPreviewPlaceholders(result.placeholders);
            if (result.placeholders.length > 0) {
              setUploadClientSlugKey(pickDefaultClientSlugKey(result.placeholders));
            }
            setPreviewError(null);
          } catch (err: any) {
            if (previewRequestRef.current !== requestId) return;
            setPreviewPlaceholders(null);
            setPreviewError(logApiError('contracts:preview', err, 'Не удалось прочитать шаблон.'));
          } finally {
            if (previewRequestRef.current === requestId) {
              setPreviewLoading(false);
            }
          }
        })();
      }, 300);
      // храним таймер на cleanup
      return () => window.clearTimeout(timer);
    }
    return undefined;
  };

  const updatePreviewPlaceholder = (index: number, patch: Partial<ContractPlaceholder>) => {
    setPreviewPlaceholders((prev) =>
      prev ? prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) : prev,
    );
  };

  const removePreviewPlaceholder = (index: number) => {
    setPreviewPlaceholders((prev) => (prev ? prev.filter((_, idx) => idx !== index) : prev));
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadName('');
    setUploadNextNumber('1');
    setUploadNumberPlaceholderKey('номер_договора');
    setUploadClientSlugKey('customer_name');
    setPreviewPlaceholders(null);
    setPreviewLoading(false);
    setPreviewError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile) {
      setError('Выберите .docx или .pdf шаблон.');
      return;
    }
    if (!uploadName.trim()) {
      setError('Укажите имя шаблона.');
      return;
    }
    const parsedNextNumber = Number(uploadNextNumber);
    if (!Number.isInteger(parsedNextNumber) || parsedNextNumber < 1) {
      setError('Стартовый номер должен быть целым числом больше нуля.');
      return;
    }
    if (!uploadNumberPlaceholderKey.trim()) {
      setError('Укажите плейсхолдер номера договора.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const created = await apiClient.uploadContractTemplate({
        file: uploadFile,
        name: uploadName.trim(),
        placeholderPrefix: '',
        placeholderSyntax: uploadSourceFormat === 'pdf' ? 'pdf_fields' : 'square_brackets',
        nextNumber: parsedNextNumber,
        numberPlaceholderKey: uploadNumberPlaceholderKey.trim(),
        clientSlugKey: uploadClientSlugKey.trim(),
        placeholders: previewPlaceholders && previewPlaceholders.length > 0
          ? previewPlaceholders.map((item, idx) => ({
              key: item.key,
              label: item.label || item.key,
              kind: item.kind,
              required: Boolean(item.required),
              example: item.example ?? null,
              order: idx,
              hint: item.hint ?? null,
              auto_fill: item.auto_fill ?? null,
              select_options: normalizeSelectOptions(item.select_options),
            }))
          : undefined,
      });
      resetUploadForm();
      setSuccessMessage(`Шаблон «${created.name}» загружен.`);
      setUploadPanelOpen(false);
      setActiveTab('create');
      await loadTemplates(created.id);
    } catch (err: any) {
      setError(logApiError('contracts:upload-template', err, 'Не удалось загрузить шаблон.'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveTemplateSettings = async (templateId: number) => {
    const detail = editingTemplateId === templateId && selectedTemplate?.id === templateId
      ? selectedTemplate
      : null;
    const summary = templates.find((item) => item.id === templateId);
    const tpl = detail ?? summary;
    if (!tpl) return;

    if (tpl.source_format === 'docx'
      && tpl.placeholder_syntax === 'prefix'
      && !templatePrefix.trim()
    ) {
      setError('Префикс плейсхолдеров не может быть пустым.');
      return;
    }
    const parsedNextNumber = Number(templateNextNumber);
    if (!Number.isInteger(parsedNextNumber) || parsedNextNumber < 1) {
      setError('Следующий номер должен быть целым числом больше нуля.');
      return;
    }
    if (!templateNumberPlaceholderKey.trim()) {
      setError('Плейсхолдер номера договора не может быть пустым.');
      return;
    }

    setSavingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await apiClient.updateContractTemplate(templateId, {
        name: templateName.trim(),
        placeholder_prefix:
          tpl.source_format === 'docx' && tpl.placeholder_syntax === 'prefix'
            ? templatePrefix.trim()
            : '',
        placeholder_syntax: tpl.placeholder_syntax,
        next_number: parsedNextNumber,
        number_placeholder_key: templateNumberPlaceholderKey.trim(),
        client_slug_key: templateClientSlugKey.trim(),
      });
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? toTemplateSummary(updated) : item)));
      if (selectedTemplate?.id === updated.id) {
        setSelectedTemplate(updated);
      }
      const cachedValues = storedValues[String(updated.id)] || {};
      setValues({
        ...createEmptyValues(updated.placeholders),
        ...cachedValues,
      });
      setEditingTemplateId(null);
      setSuccessMessage(`Шаблон «${updated.name}» сохранён.`);
    } catch (err: any) {
      setError(logApiError('contracts:save-template', err, 'Не удалось обновить шаблон.'));
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleEditToggle = async (templateId: number) => {
    if (editingTemplateId === templateId) {
      setEditingTemplateId(null);
      return;
    }
    setEditingTemplateId(templateId);
    setError(null);
    setSuccessMessage(null);
    // загружаем полный шаблон, чтобы получить актуальные плейсхолдеры
    try {
      const detail = await apiClient.getContractTemplate(templateId);
      setTemplateName(detail.name);
      setTemplatePrefix(detail.placeholder_prefix || '');
      setTemplateNextNumber(String(detail.next_number || 1));
      setTemplateNumberPlaceholderKey(detail.number_placeholder_key || 'contract_number');
      setTemplateClientSlugKey(detail.client_slug_key || 'customer_name');
      setPlaceholderEdits(
        detail.placeholders.map((item) => ({
          ...item,
          hint: item.hint ?? null,
          auto_fill: item.auto_fill ?? null,
          select_options: Array.isArray(item.select_options) ? [...item.select_options] : [],
        })),
      );
    } catch (err: any) {
      setError(logApiError('contracts:edit-toggle', err, 'Не удалось загрузить шаблон.'));
      setEditingTemplateId(null);
    }
  };

  const handlePlaceholderEdit = (key: string, patch: Partial<ContractPlaceholder>) => {
    setPlaceholderEdits((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  };

  const handleSavePlaceholders = async (templateId: number) => {
    if (placeholderEdits.length === 0) return;
    setPlaceholdersSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await apiClient.patchContractTemplatePlaceholders(
        templateId,
        placeholderEdits.map((item) => ({
          ...item,
          select_options: normalizeSelectOptions(item.select_options),
        })),
      );
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? toTemplateSummary(updated) : item)));
      if (selectedTemplate?.id === updated.id) {
        setSelectedTemplate(updated);
        // Перезальём values по обновлённым плейсхолдерам — ключи могли поменяться.
        const contractNumberValue = String(updated.next_number || 1);
        const nextMap = buildAutoFillMap(updated);
        const baseValues: Record<string, string> = {
          ...createEmptyValues(updated.placeholders),
          ...(storedValues[String(updated.id)] || {}),
        };
        setValues(applyAutoFill(baseValues, nextMap, autoTouchedKeys, contractNumberValue, todayParts));
      }
      setSuccessMessage(`Поля шаблона «${updated.name}» обновлены.`);
    } catch (err: any) {
      setError(logApiError('contracts:save-placeholders', err, 'Не удалось сохранить поля шаблона.'));
    } finally {
      setPlaceholdersSaving(false);
    }
  };

  const handleCancelPlaceholderEdit = async (templateId: number) => {
    setEditingTemplateId(null);
    setPlaceholderEdits([]);
    setError(null);
    // Подтянем свежий state, чтобы отменить локальные изменения в input'ах базовых настроек.
    try {
      const detail = await apiClient.getContractTemplate(templateId);
      setTemplateName(detail.name);
      setTemplatePrefix(detail.placeholder_prefix || '');
      setTemplateNextNumber(String(detail.next_number || 1));
      setTemplateNumberPlaceholderKey(detail.number_placeholder_key || 'contract_number');
      setTemplateClientSlugKey(detail.client_slug_key || 'customer_name');
    } catch {
      // мягкое игнорирование — пользователь закрыл без сохранения
    }
  };

  const handleDownloadTemplateFile = async (templateId: number) => {
    setDownloadingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.downloadContractTemplateFile(templateId);
      downloadBlobResult(result);
      setSuccessMessage(`Исходный шаблон «${result.filename}» скачан.`);
    } catch (err: any) {
      setError(logApiError('contracts:download-template', err, 'Не удалось скачать шаблон.'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const triggerReplaceFile = (templateId: number) => {
    replaceFileTemplateIdRef.current = templateId;
    setReplaceFileTemplateId(templateId);
    setReplaceFile(null);
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setReplaceFile(file);
  };

  const handleReplaceTemplateFile = async (templateId: number) => {
    if (!replaceFile) {
      setError('Выберите файл для замены текущего шаблона.');
      return;
    }
    const summary = templates.find((item) => item.id === templateId);
    if (!summary) return;
    if (replaceSourceFormat !== summary.source_format) {
      setError(`Нужен файл ${getFormatLabel(summary.source_format)}. Смену формата лучше делать новым шаблоном.`);
      return;
    }

    setReplacingTemplate(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await apiClient.replaceContractTemplateFile(templateId, replaceFile);
      setReplaceFile(null);
      setReplaceFileTemplateId(null);
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = '';
      }
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? toTemplateSummary(updated) : item)));
      if (selectedTemplate?.id === updated.id) {
        setSelectedTemplate(updated);
        setValues({
          ...createEmptyValues(updated.placeholders),
          ...(storedValues[String(updated.id)] || {}),
        });
      }
      setSuccessMessage(`Файл шаблона «${updated.name}» заменён.`);
      if (editingTemplateId === templateId) {
        setTemplateName(updated.name);
        setTemplatePrefix(updated.placeholder_prefix || '');
        setTemplateNextNumber(String(updated.next_number || 1));
        setTemplateNumberPlaceholderKey(updated.number_placeholder_key || 'contract_number');
        setTemplateClientSlugKey(updated.client_slug_key || 'customer_name');
      }
      await loadTemplates(templateId === selectedTemplateId ? templateId : selectedTemplateId ?? undefined);
    } catch (err: any) {
      setError(logApiError('contracts:replace-file', err, 'Не удалось заменить файл шаблона.'));
    } finally {
      setReplacingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    if (!window.confirm(`Удалить шаблон «${template.name}»? Это действие необратимо.`)) {
      return;
    }

    setDeletingTemplateId(templateId);
    setError(null);
    setSuccessMessage(null);
    try {
      await apiClient.deleteContractTemplate(templateId);
      const nextStoredValues = { ...storedValues };
      delete nextStoredValues[String(templateId)];
      saveStoredValues(nextStoredValues);
      setStoredValues(nextStoredValues);
      if (editingTemplateId === templateId) setEditingTemplateId(null);
      setSuccessMessage(`Шаблон «${template.name}» удалён.`);
      await loadTemplates(templateId === selectedTemplateId ? undefined : selectedTemplateId ?? undefined);
    } catch (err: any) {
      setError(logApiError('contracts:delete-template', err, 'Не удалось удалить шаблон.'));
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleValueChange = (key: string, nextValue: string) => {
    setValues((prev) => ({ ...prev, [key]: nextValue }));
    if (autoFillMap[key]) {
      setAutoTouchedKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }
    setValidationKeys((prev) => prev.filter((item) => item !== key));
    setError(null);
  };

  const handleResetValues = () => {
    if (!selectedTemplate) return;
    const empty = createEmptyValues(selectedTemplate.placeholders);
    const contractNumberValue = String(selectedTemplate.next_number || 1);
    const nextMap = buildAutoFillMap(selectedTemplate);
    setValues(applyAutoFill(empty, nextMap, {}, contractNumberValue, todayParts));
    setAutoTouchedKeys({});
    setValidationKeys([]);
    setSuccessMessage(null);
    setViewingGeneration(null);
  };

  const handleCopyLastValues = () => {
    if (!selectedTemplate || !selectedTemplateId || !lastValuesForSelectedTemplate) return;
    const merged = {
      ...createEmptyValues(selectedTemplate.placeholders),
      ...lastValuesForSelectedTemplate,
    };
    // Если в прошлый раз номер был другой — он попадёт в cachedValues и перепишет auto.
    const contractNumberValue = String(selectedTemplate.next_number || 1);
    const nextMap = buildAutoFillMap(selectedTemplate);
    setValues(applyAutoFill(merged, nextMap, {}, contractNumberValue, todayParts));
    setAutoTouchedKeys({});
    setValidationKeys([]);
    setViewingGeneration(null);
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !selectedTemplateId) {
      setError('Сначала выберите шаблон.');
      return;
    }

    const liveValues = applyAutoFill(
      values,
      autoFillMap,
      autoTouchedKeys,
      String(selectedTemplate.next_number || 1),
      todayParts,
    );

    const missingKeys = fillablePlaceholders
      .filter((item) => item.required && !String(liveValues[item.key] || '').trim())
      .map((item) => item.key);

    if (missingKeys.length > 0) {
      setValidationKeys(missingKeys);
      setError('Заполните обязательные поля.');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.generateContractTemplate(selectedTemplateId, {
        values: liveValues,
        output_format: selectedTemplate.source_format === 'docx' ? outputFormat : 'pdf',
      });
      downloadBlobResult(result);

      const nextStoredValues = {
        ...storedValues,
        [String(selectedTemplateId)]: liveValues,
      };
      setStoredValues(nextStoredValues);
      saveStoredValues(nextStoredValues);
      setSuccessMessage(`Файл «${result.filename}» готов.`);
      setViewingGeneration(null);
      await loadTemplates(selectedTemplateId, nextStoredValues);
    } catch (err: any) {
      const message = logApiError('contracts:generate', err, 'Не удалось сгенерировать договор.');
      const detailData = (err?.response?.data?.detail ?? err?.response?.data) as any;
      const missing = detailData?.missing_keys;
      if (Array.isArray(missing)) {
        setValidationKeys(missing);
      }
      setError(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (generation: ContractGenerationSummary) => {
    setRegeneratingGenerationId(generation.id);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await apiClient.regenerateContractGeneration(generation.id, {
        output_format: generation.output_format,
      });
      downloadBlobResult(result);
      setSuccessMessage(`Файл «${result.filename}» пересобран из истории.`);
      if (selectedTemplateId) {
        await loadGenerations(selectedTemplateId);
      }
    } catch (err: any) {
      setError(logApiError('contracts:regenerate', err, 'Не удалось пересобрать договор из истории.'));
    } finally {
      setRegeneratingGenerationId(null);
    }
  };

  const handleViewGeneration = async (generation: ContractGenerationSummary) => {
    if (!selectedTemplate) return;
    setLoadingGenerationId(generation.id);
    setError(null);
    setSuccessMessage(null);
    try {
      const detail = await apiClient.getContractGeneration(generation.id);
      // Исторический просмотр: не применяем авто-подстановки, чтобы видеть
      // именно те значения, которые ушли в документ.
      setValues({
        ...createEmptyValues(selectedTemplate.placeholders),
        ...detail.values,
      });
      setAutoTouchedKeys({});
      setValidationKeys([]);
      setViewingGeneration(generation);
      setSuccessMessage(`Загружен договор № ${generation.contract_number}`);
    } catch (err: any) {
      setError(logApiError('contracts:view-generation', err, 'Не удалось загрузить детали договора.'));
    } finally {
      setLoadingGenerationId(null);
    }
  };

  const renderField = (placeholder: ContractPlaceholder) => {
    const value = values[placeholder.key] ?? '';
    const invalid = validationKeys.includes(placeholder.key);
    const fieldId = getFieldElementId(placeholder.key);
    const inputHint = placeholder.hint?.trim() || placeholder.example?.trim() || placeholder.label;

    if (placeholder.kind === 'textarea') {
      return (
        <div className="cg-field-control-wrap">
          <textarea
            id={fieldId}
            className={`cg-input cg-textarea ${invalid ? 'is-invalid' : ''}`}
            value={value}
            onChange={(event) => handleValueChange(placeholder.key, event.target.value)}
            rows={4}
            placeholder={inputHint}
          />
        </div>
      );
    }

    if (placeholder.kind === 'select') {
      const options = (placeholder.select_options || []).filter((opt) => typeof opt === 'string');
      return (
        <div className="cg-field-control-wrap">
          <Select
            value={value || undefined}
            onValueChange={(next) => handleValueChange(placeholder.key, next)}
          >
            <SelectTrigger id={fieldId} className={`cg-input cg-select-trigger ${invalid ? 'is-invalid' : ''}`}>
              <SelectValue placeholder={inputHint || 'Выберите значение'} />
            </SelectTrigger>
            <SelectContent className="cg-select-content">
              {options.length === 0 ? (
                <SelectItem value="__empty__" disabled>
                  Нет вариантов — заполните в настройках шаблона
                </SelectItem>
              ) : (
                options.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return (
      <div className="cg-field-control-wrap">
        <input
          id={fieldId}
          className={`cg-input ${invalid ? 'is-invalid' : ''}`}
          value={value}
          onChange={(event) => handleValueChange(placeholder.key, event.target.value)}
          placeholder={inputHint}
        />
      </div>
    );
  };

  const renderTemplateSettings = (templateId: number) => {
    const summary = templates.find((item) => item.id === templateId);
    if (!summary) return null;
    const showPrefix = summary.source_format === 'docx' && summary.placeholder_syntax === 'prefix';

    return (
      <div className="cg-settings-panel">
        <div className="cg-settings-section">
          <h4 className="cg-settings-section-title">Основные настройки</h4>
          <div className="cg-settings-grid">
            <label className="cg-field">
              <span className="cg-label">Название</span>
              <input
                className="cg-input"
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder="Имя шаблона"
              />
            </label>
            <label className="cg-field">
              <span className="cg-label">Стартовый №</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="cg-input cg-input--no-spinner"
                value={templateNextNumber}
                onChange={(event) => setTemplateNextNumber(event.target.value)}
              />
            </label>
            <label className="cg-field">
              <span className="cg-label">Ключ номера</span>
              <input
                className="cg-input cg-input--mono"
                value={templateNumberPlaceholderKey}
                onChange={(event) => setTemplateNumberPlaceholderKey(event.target.value)}
                placeholder="contract_number"
              />
            </label>
            <label className="cg-field">
              <span className="cg-label">Ключ клиента (технический key)</span>
              <Select
                value={templateClientSlugKey}
                onValueChange={setTemplateClientSlugKey}
              >
                <SelectTrigger className="cg-input cg-select-trigger cg-input--mono">
                  <SelectValue placeholder="customer_name" />
                </SelectTrigger>
                <SelectContent className="cg-select-content">
                  {placeholderEdits.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const matched = placeholderEdits.find(p => p.key === templateClientSlugKey);
                return matched ? (
                  <span className="cg-hint">{matched.label}</span>
                ) : null;
              })()}
            </label>
            {showPrefix && (
              <label className="cg-field cg-field--wide">
                <span className="cg-label">Префикс плейсхолдеров</span>
                <input
                  className="cg-input cg-input--mono"
                  value={templatePrefix}
                  onChange={(event) => setTemplatePrefix(event.target.value)}
                  placeholder="##"
                />
              </label>
            )}
          </div>
          <div className="cg-settings-actions">
            <button
              type="button"
              className="cg-btn cg-btn--ghost"
              onClick={() => void handleCancelPlaceholderEdit(templateId)}
              disabled={savingTemplate || placeholdersSaving}
            >
              Отмена
            </button>
            <button
              type="button"
              className="cg-btn cg-btn--primary"
              onClick={() => void handleSaveTemplateSettings(templateId)}
              disabled={savingTemplate || placeholdersSaving}
            >
              <Save size={15} />
              {savingTemplate ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </div>

        {placeholderEdits.length > 0 && (
          <div className="cg-settings-section">
            <h4 className="cg-settings-section-title">
              Поля шаблона · подсказки, селекты, автоподстановки
            </h4>
            <div className="cg-placeholder-list">
              {placeholderEdits.map((placeholder) => {
                const isSelect = placeholder.kind === 'select';
                return (
                  <div key={placeholder.key} className="cg-placeholder-row">
                    <div className="cg-placeholder-row-head">
                      <span className="cg-placeholder-key">{placeholder.key}</span>
                      {placeholder.required && <span className="cg-req">*</span>}
                    </div>

                    <label className="cg-field">
                      <span className="cg-label">Название для пользователя</span>
                      <input
                        className="cg-input"
                        value={placeholder.label}
                        onChange={(event) => handlePlaceholderEdit(placeholder.key, { label: event.target.value })}
                      />
                    </label>

                    <div className="cg-placeholder-row-controls">
                      <label className="cg-field">
                        <span className="cg-label">Тип поля</span>
                        <Select
                          value={placeholder.kind}
                          onValueChange={(value) => {
                            handlePlaceholderEdit(placeholder.key, { kind: value as PlaceholderKind });
                            if (value === 'select') {
                              const idx = placeholderEdits.findIndex((p) => p.key === placeholder.key);
                              window.setTimeout(() => {
                                if (idx >= 0) settingsOptionsTextareaRefs.current[idx]?.focus();
                              }, 50);
                            }
                          }}
                        >
                          <SelectTrigger className="cg-input cg-select-trigger">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="cg-select-content">
                            <SelectItem value="text">текст</SelectItem>
                            <SelectItem value="textarea">большой текст</SelectItem>
                            <SelectItem value="select">выпадающий список</SelectItem>
                          </SelectContent>
                        </Select>
                      </label>

                      <label className="cg-field cg-field--inline-check">
                        <input
                          type="checkbox"
                          checked={placeholder.required}
                          onChange={(event) =>
                            handlePlaceholderEdit(placeholder.key, { required: event.target.checked })
                          }
                        />
                        <span>Обязательное</span>
                      </label>
                    </div>

                    <label className="cg-field cg-field--wide">
                      <span className="cg-label">Подсказка/пример</span>
                      <textarea
                        rows={3}
                        className="cg-input cg-textarea cg-textarea--small"
                        value={placeholder.hint || ''}
                        onChange={(event) =>
                          handlePlaceholderEdit(placeholder.key, {
                            hint: event.target.value || null,
                          })
                        }
                        placeholder="Например: Как в паспорте, полностью — Иванов Иван Иванович"
                      />
                      <span className="cg-hint">
                        Покажется пользователю под полем при заполнении договора.
                      </span>
                    </label>

                    {isSelect && (
                      <label className="cg-field">
                        <span className="cg-label">Варианты (по одному в строке)</span>
                        <textarea
                          ref={(el) => {
                            const idx = placeholderEdits.findIndex((p) => p.key === placeholder.key);
                            if (idx >= 0) settingsOptionsTextareaRefs.current[idx] = el;
                          }}
                          rows={4}
                          className="cg-input cg-textarea cg-textarea--small cg-textarea--mono"
                          value={(placeholder.select_options || []).join('\n')}
                          onChange={(event) =>
                            handlePlaceholderEdit(placeholder.key, {
                              select_options: event.target.value
                                .split('\n'),
                            })
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') event.stopPropagation();
                          }}
                          placeholder={'ООО\nИП\nСамозанятый'}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="cg-settings-actions">
              <button
                type="button"
                className="cg-btn cg-btn--primary"
                onClick={() => void handleSavePlaceholders(templateId)}
                disabled={placeholdersSaving || savingTemplate}
              >
                <Save size={15} />
                {placeholdersSaving ? 'Сохранение полей…' : 'Сохранить поля шаблона'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const totalTemplates = templates.length;
  const ctaLabel = (() => {
    if (!selectedTemplate) return 'Скачать';
    return `Скачать ${getFormatLabel(
      selectedTemplate.source_format === 'docx' ? outputFormat : selectedTemplate.source_format,
    )}`;
  })();

  // ─── Рендер ────────────────────────────────────────────────
  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (event.key !== 'Enter') return;
    // Enter в textarea — оставляем дефолт (перевод строки), но блокируем propagation,
    // чтобы Radix Dialog / фокус-менеджер не перехватывали и не сбрасывали фокус.
    // Enter в input, который НЕ submit-кнопка — то же самое: обычные <input type="text">
    // не сабмитят форму, но Radix иногда делает акцент на trigger; здесь безопасно просто
    // не дать событию всплыть.
    if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
      event.stopPropagation();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent
        className="contract-generator__dialog"
        aria-describedby={undefined}
        onKeyDown={handleDialogKeyDown}
      >
        <DialogTitle className="cg-sr-only">Договоры</DialogTitle>

        {/* Шапка: заголовок + табы */}
        <header className="cg-header">
          <div className="cg-header-title">
            <h1>Договоры</h1>
            <p>Сборка документов по шаблонам</p>
          </div>
          <nav className="cg-tabs" role="tablist" aria-label="Разделы договоров">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'create'}
              className={`cg-tab ${activeTab === 'create' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <FileText size={16} />
              Создать договор
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'templates'}
              className={`cg-tab ${activeTab === 'templates' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              <Settings size={16} />
              Шаблоны
              <span className="cg-tab-count">{totalTemplates}</span>
            </button>
          </nav>
        </header>

        {/* Тело модала: меняется по табу */}
        <main className={`cg-body ${activeTab === 'create' ? '' : 'cg-body--templates'}`}>

          {/* ──────── ВКЛАДКА: СОЗДАТЬ ДОГОВОР ──────── */}
          {activeTab === 'create' && (
            <>
              <section className="cg-section">
                <h2 className="cg-section-title">1. Шаблон</h2>
                {listLoading && templates.length === 0 ? (
                  <div className="cg-empty cg-empty--compact">
                    <strong>Загрузка шаблонов…</strong>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="cg-empty cg-empty--block">
                    <strong>Шаблонов пока нет</strong>
                    <span>Перейдите на вкладку «Шаблоны» и добавьте DOCX или PDF.</span>
                  </div>
                ) : (
                  <div className="cg-template-grid">
                    {templates.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`cg-template-card ${selectedTemplateId === item.id ? 'is-active' : ''}`}
                        onClick={() => setSelectedTemplateId(item.id)}
                      >
                        <span className={`cg-format-pill cg-format-pill--${item.source_format}`}>
                          {getFormatLabel(item.source_format)}
                        </span>
                        <span className="cg-template-name">{item.name}</span>
                        <span className="cg-template-meta">
                          {item.placeholders_count} {item.placeholders_count === 1 ? 'поле' : 'полей'} · след. № {item.next_number}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="cg-add-template"
                      onClick={() => {
                        setActiveTab('templates');
                        setUploadPanelOpen(true);
                      }}
                    >
                      <Plus size={18} />
                      Добавить шаблон
                    </button>
                  </div>
                )}
              </section>

              <section className="cg-section">
                <h2 className="cg-section-title">2. Заполните данные</h2>

                {!selectedTemplate ? (
                  <div className="cg-empty cg-empty--tall">
                    <strong>Нет активного шаблона</strong>
                    <span>Выберите шаблон выше или добавьте новый.</span>
                  </div>
                ) : (
                  <div className="cg-form-card">
                    <div className="cg-form-header">
                      <div>
                        <h3>{selectedTemplate.name}</h3>
                        <p>
                          Номер присвоится автоматически —{' '}
                          <strong>№ {selectedTemplate.next_number}</strong>
                          {detailLoading && ' · читаю шаблон…'}
                          {viewingGeneration && (
                            <>
                              {' · '}
                              <span className="cg-viewing-badge">
                                Просмотр № {viewingGeneration.contract_number}
                              </span>
                            </>
                          )}
                        </p>
                      </div>

                      <div className="cg-form-meta">
                        {fillableRequiredCount > 0 && (
                          <div className="cg-progress">
                            <span>{filledRequiredCount} / {fillableRequiredCount} обязательных</span>
                            <div className="cg-progress-bar">
                              <span
                                style={{
                                  width: `${fillableRequiredCount === 0
                                    ? 100
                                    : (filledRequiredCount / fillableRequiredCount) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {numberPlaceholderMissing && (
                      <div className="cg-notice cg-notice--warning">
                        В шаблоне нет поля «{selectedTemplate.number_placeholder_key}»: номер сохранится в истории, но не попадёт в документ.
                      </div>
                    )}

                    {fillablePlaceholders.length === 0 ? (
                      <div className="cg-empty cg-empty--compact">
                        <strong>Нет полей для ручного ввода</strong>
                        <span>Можно сразу скачать договор с автоматическим номером.</span>
                      </div>
                    ) : (
                      <div className="cg-fields">
                        {fillablePlaceholders.map((placeholder) => {
                          const invalid = validationKeys.includes(placeholder.key);
                          const fieldId = getFieldElementId(placeholder.key);
                          const isWide = placeholder.kind === 'textarea';
                          return (
                            <div
                              key={placeholder.key}
                              className={`cg-field ${isWide ? 'cg-field--wide' : ''}`}
                            >
                              <label className="cg-key-title" htmlFor={fieldId}>
                                <code className="cg-key-badge">{placeholder.key}</code>
                                {placeholder.required && <span className="cg-req">*</span>}
                              </label>
                              {renderField(placeholder)}
                              {invalid && (
                                <span className="cg-field-error">Обязательное поле</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedTemplate.placeholders.length > 0 && (
                      <div className="cg-form-actions">
                        <button
                          type="button"
                          className="cg-btn cg-btn--ghost"
                          onClick={handleCopyLastValues}
                          disabled={!lastValuesForSelectedTemplate || generating}
                          title="Подставить значения из предыдущего договора этого шаблона"
                        >
                          <RotateCcw size={14} />
                          Прошлые значения
                        </button>
                        <button
                          type="button"
                          className="cg-btn cg-btn--ghost"
                          onClick={handleResetValues}
                          disabled={generating}
                          title="Очистить все поля"
                        >
                          <RefreshCw size={14} />
                          Сбросить
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* История */}
              {selectedTemplate && (
                <section className="cg-section">
                  <h2 className="cg-section-title">История по этому шаблону</h2>
                  {generationsLoading && generations.length === 0 ? (
                    <div className="cg-empty cg-empty--compact">
                      <strong>Загружаю историю…</strong>
                    </div>
                  ) : generations.length === 0 ? (
                    <div className="cg-empty cg-empty--compact">
                      <strong>Пока пусто</strong>
                      <span>Созданные договоры появятся здесь.</span>
                    </div>
                  ) : (
                    <div className="cg-recent-list">
                      {generations.map((generation) => (
                        (() => {
                          const clientDataEntries = getClientDataEntries(generation);
                          return (
                            <div
                              key={generation.id}
                              className={`cg-recent-row ${viewingGeneration?.id === generation.id ? 'is-viewing' : ''}`}
                            >
                              <div className="cg-recent-num">№ {generation.contract_number}</div>
                              <div className="cg-recent-main">
                                <strong>
                                  {generation.client_slug || `Договор № ${generation.contract_number}`}
                                </strong>
                                <span>{formatDateRu(generation.created_at)}</span>
                                {clientDataEntries.length > 0 && (
                                  <div className="cg-recent-client-data" aria-label="Данные клиента">
                                    {clientDataEntries.map(([key, value]) => (
                                      <span key={key} className="cg-recent-client-data-item">
                                        <b>{key}</b>
                                        {value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className={`cg-format-pill cg-format-pill--${generation.output_format}`}>
                                {getFormatLabel(generation.output_format)}
                              </span>
                              <button
                                type="button"
                                className="cg-icon-btn"
                                onClick={() => void handleViewGeneration(generation)}
                                disabled={loadingGenerationId === generation.id || regeneratingGenerationId === generation.id}
                                title="Загрузить значения"
                                aria-label="Загрузить значения"
                              >
                                <FileText size={16} />
                              </button>
                              <button
                                type="button"
                                className="cg-icon-btn"
                                onClick={() => void handleRegenerate(generation)}
                                disabled={regeneratingGenerationId === generation.id || loadingGenerationId === generation.id}
                                title="Скачать"
                                aria-label="Скачать"
                              >
                                <Download size={16} />
                              </button>
                              {generation.template_changed && (
                                <span className="cg-recent-warning" title="Шаблон изменился после генерации">
                                  шаблон изменён
                                </span>
                              )}
                            </div>
                          );
                        })()
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          )}

          {/* ──────── ВКЛАДКА: ШАБЛОНЫ (АДМИНКА) ──────── */}
          {activeTab === 'templates' && (
            <>
              <div className="cg-toolbar">
                <div className="cg-search">
                  <Search size={16} aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Поиск шаблона…"
                    className="cg-search-input"
                    aria-label="Поиск шаблона"
                  />
                </div>
                <button
                  type="button"
                  className="cg-btn cg-btn--primary"
                  onClick={() => setUploadPanelOpen((prev) => !prev)}
                >
                  {uploadPanelOpen ? <X size={16} /> : <Plus size={16} />}
                  {uploadPanelOpen ? 'Скрыть форму' : 'Добавить шаблон'}
                </button>
              </div>

              {uploadPanelOpen && (
                <div className="cg-upload-card">
                  <h3 className="cg-upload-title">Новый шаблон</h3>

                  <div className="cg-fields cg-fields--two">
                    <label className="cg-field cg-field--wide">
                      <span className="cg-label">Файл (.docx или .pdf)</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                        className="cg-file-input"
                        onChange={handleUploadFileChange}
                      />
                      {uploadFile && uploadSourceFormat && (
                        <span className="cg-hint">
                          Выбрано: {uploadFile.name} — {getFormatLabel(uploadSourceFormat)}
                        </span>
                      )}
                    </label>

                    <label className="cg-field cg-field--wide">
                      <span className="cg-label">Название</span>
                      <input
                        type="text"
                        className="cg-input"
                        value={uploadName}
                        onChange={(event) => setUploadName(event.target.value)}
                        placeholder="Договор монтажа"
                      />
                    </label>

                    <label className="cg-field">
                      <span className="cg-label">Первый номер</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="cg-input cg-input--no-spinner"
                        value={uploadNextNumber}
                        onChange={(event) => setUploadNextNumber(event.target.value)}
                      />
                    </label>

                    <label className="cg-field">
                      <span className="cg-label">Ключ номера</span>
                      <input
                        type="text"
                        className="cg-input cg-input--mono"
                        value={uploadNumberPlaceholderKey}
                        onChange={(event) => setUploadNumberPlaceholderKey(event.target.value)}
                        placeholder="contract_number"
                      />
                    </label>

                    <label className="cg-field">
                      <span className="cg-label">Ключ клиента (технический key)</span>
                      {previewPlaceholders && previewPlaceholders.length > 0 ? (
                        <>
                          <Select
                            value={uploadClientSlugKey}
                            onValueChange={setUploadClientSlugKey}
                          >
                            <SelectTrigger className="cg-input cg-select-trigger cg-input--mono">
                              <SelectValue placeholder="customer_name" />
                            </SelectTrigger>
                            <SelectContent className="cg-select-content">
                              {previewPlaceholders.map((placeholder) => (
                                <SelectItem key={placeholder.key} value={placeholder.key}>
                                  {placeholder.key}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(() => {
                            const matched = previewPlaceholders.find(p => p.key === uploadClientSlugKey);
                            return matched ? (
                              <span className="cg-hint">{matched.label}</span>
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <input
                          type="text"
                          className="cg-input cg-input--mono"
                          value={uploadClientSlugKey}
                          onChange={(event) => setUploadClientSlugKey(event.target.value)}
                          placeholder="customer_name"
                        />
                      )}
                    </label>
                  </div>

                  <div className="cg-upload-preview">
                    <div className="cg-upload-preview-head">
                      <h4 className="cg-settings-section-title">Поля шаблона</h4>
                      {uploadFile && (
                        <p className="cg-hint">
                          {previewLoading ? (
                            'Читаю шаблон…'
                          ) : previewError ? (
                            <span className="cg-preview-error">{previewError}</span>
                          ) : previewPlaceholders === null ? (
                            <>Обрамите нужные места квадратными скобками в документе, например <code>[число]</code> и <code>[месяц]</code>, затем выберите файл.</>
                          ) : previewPlaceholders.length === 0 ? (
                            <>Шаблон загружен, но в нём не нашлось <code>[…]</code>. Можно создать пустой шаблон и заполнить поля позже в «Настройки».</>
                          ) : (
                            <>Найдено {previewPlaceholders.length} {previewPlaceholders.length === 1 ? 'поле' : (previewPlaceholders.length < 5 ? 'поля' : 'полей')}. Проверьте типы, авто-подстановки и обязательность перед сохранением.</>
                          )}
                        </p>
                      )}
                    </div>

                    {previewPlaceholders !== null && previewPlaceholders.length > 0 && (
                      <div className="cg-placeholder-list">
                        {previewPlaceholders.map((placeholder, index) => (
                          <div key={`${placeholder.key}-${index}`} className="cg-placeholder-row cg-preview-row">
                            <div className="cg-placeholder-row-head">
                              <span className="cg-placeholder-key">{placeholder.key}</span>
                              {placeholder.required && <span className="cg-req">*</span>}
                              <button
                                type="button"
                                className="cg-icon-btn cg-icon-btn--danger cg-placeholder-remove"
                                onClick={() => removePreviewPlaceholder(index)}
                                aria-label="Удалить поле"
                                title="Удалить поле"
                                disabled={uploading}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>

                            <label className="cg-field">
                              <span className="cg-label">Название для пользователя</span>
                              <input
                                className="cg-input"
                                value={placeholder.label}
                                onChange={(event) => updatePreviewPlaceholder(index, { label: event.target.value })}
                                disabled={uploading}
                              />
                            </label>

                            <div className="cg-placeholder-row-controls">
                              <label className="cg-field">
                                <span className="cg-label">Тип поля</span>
                                <Select
                                  value={placeholder.kind}
                                  onValueChange={(value) => {
                                    updatePreviewPlaceholder(index, { kind: value as PlaceholderKind });
                                    if (value === 'select') {
                                      window.setTimeout(() => {
                                        previewOptionsTextareaRefs.current[index]?.focus();
                                      }, 50);
                                    }
                                  }}
                                  disabled={uploading}
                                >
                                  <SelectTrigger className="cg-input cg-select-trigger">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="cg-select-content">
                                    <SelectItem value="text">текст</SelectItem>
                                    <SelectItem value="textarea">большой текст</SelectItem>
                                    <SelectItem value="select">выпадающий список</SelectItem>
                                  </SelectContent>
                                </Select>
                              </label>

                              <label className="cg-field cg-field--inline-check">
                                <input
                                  type="checkbox"
                                  checked={placeholder.required}
                                  onChange={(event) =>
                                    updatePreviewPlaceholder(index, { required: event.target.checked })
                                  }
                                  disabled={uploading}
                                />
                                <span>Обязательное</span>
                              </label>
                            </div>

                            {placeholder.kind === 'select' && (
                              <label className="cg-field">
                                <span className="cg-label">Варианты (по одному в строке)</span>
                                <textarea
                                  ref={(el) => { previewOptionsTextareaRefs.current[index] = el; }}
                                  rows={4}
                                  className="cg-input cg-textarea cg-textarea--small cg-textarea--mono"
                                  value={(placeholder.select_options || []).join('\n')}
                                  onChange={(event) =>
                                    updatePreviewPlaceholder(index, {
                                      select_options: event.target.value
                                        .split('\n'),
                                    })
                                  }
                                  onKeyDown={(event) => {
                                    // Не даём родительским контейнерам перехватить Enter.
                                    if (event.key === 'Enter') event.stopPropagation();
                                  }}
                                  disabled={uploading}
                                  placeholder={'ООО\nИП\nСамозанятый'}
                                />
                              </label>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="cg-upload-actions">
                    <button
                      type="button"
                      className="cg-btn cg-btn--ghost"
                      onClick={() => { resetUploadForm(); setUploadPanelOpen(false); setError(null); }}
                      disabled={uploading}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="cg-btn cg-btn--primary"
                      onClick={() => void handleUploadTemplate()}
                      disabled={uploading || !uploadFile || previewLoading}
                    >
                      <Upload size={15} />
                      {uploading ? 'Загружаю…' : 'Сохранить шаблон'}
                    </button>
                  </div>
                </div>
              )}

              {/* Скрытый input для замены файла шаблона */}
              <input
                ref={replaceFileInputRef}
                type="file"
                accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                style={{ display: 'none' }}
                onChange={handleReplaceFileInputChange}
              />

              {replaceFile && replaceFileTemplateId && (
                <div className="cg-notice cg-notice--warning cg-notice--inline">
                  Выбран файл {replaceFile.name} ({getFormatLabel(replaceSourceFormat || 'docx')}).{' '}
                  <button
                    type="button"
                    className="cg-link"
                    onClick={() => void handleReplaceTemplateFile(replaceFileTemplateId)}
                    disabled={replacingTemplate}
                  >
                    {replacingTemplate ? 'Заменяю…' : 'Подтвердить замену'}
                  </button>{' '}
                  <button
                    type="button"
                    className="cg-link cg-link--muted"
                    onClick={() => {
                      setReplaceFile(null);
                      setReplaceFileTemplateId(null);
                      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
                    }}
                    disabled={replacingTemplate}
                  >
                    Отменить
                  </button>
                </div>
              )}

              {listLoading && templates.length === 0 ? (
                <div className="cg-empty cg-empty--block">
                  <strong>Загрузка шаблонов…</strong>
                </div>
              ) : templates.length === 0 ? (
                <div className="cg-empty cg-empty--block">
                  <strong>Шаблонов ещё нет</strong>
                  <span>Нажмите «Добавить шаблон», чтобы загрузить DOCX или PDF.</span>
                </div>
              ) : (
                <div className="cg-tpl-list">
                  {templates.map((item) => (
                    <React.Fragment key={item.id}>
                      <article className="cg-tpl-row">
                        <span className={`cg-format-pill cg-format-pill--${item.source_format}`}>
                          {getFormatLabel(item.source_format)}
                        </span>
                        <div className="cg-tpl-info">
                          <h3>{item.name}</h3>
                          <p>
                            {item.original_filename} · {item.placeholders_count} {item.placeholders_count === 1 ? 'поле' : 'полей'} ·
                            создано {item.created_count}
                            {item.last_number !== null && ` · последний № ${item.last_number}`}
                          </p>
                        </div>
                        <div className="cg-tpl-actions">
                          <button
                            type="button"
                            className="cg-btn cg-btn--secondary"
                            onClick={() => void handleDownloadTemplateFile(item.id)}
                            disabled={downloadingTemplate}
                            title="Скачать исходный файл шаблона"
                          >
                            <Download size={14} />
                            Скачать
                          </button>
                          <button
                            type="button"
                            className="cg-btn cg-btn--ghost"
                            onClick={() => void handleEditToggle(item.id)}
                            aria-pressed={editingTemplateId === item.id}
                            title="Настройки шаблона"
                          >
                            <Settings size={14} />
                            Настройки
                          </button>
                          <button
                            type="button"
                            className="cg-icon-btn"
                            onClick={() => triggerReplaceFile(item.id)}
                            title="Заменить файл шаблона"
                            aria-label="Заменить файл"
                          >
                            <Upload size={15} />
                          </button>
                          <button
                            type="button"
                            className="cg-icon-btn cg-icon-btn--danger"
                            onClick={() => void handleDeleteTemplate(item.id)}
                            disabled={deletingTemplateId === item.id}
                            title="Удалить шаблон"
                            aria-label="Удалить шаблон"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </article>
                      {editingTemplateId === item.id && renderTemplateSettings(item.id)}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </>
          )}

        </main>

        {/* ──────── CTA-бар (только на вкладке «Создать») ──────── */}
        {activeTab === 'create' && (
          <footer className="cg-cta">
            <div className="cg-cta-msg">
              {error ? (
                <>
                  <span className="cg-dot cg-dot--danger" />
                  <span className="cg-cta-error">{error}</span>
                </>
              ) : successMessage ? (
                <>
                  <span className="cg-dot cg-dot--success" />
                  <span>{successMessage}</span>
                </>
              ) : selectedTemplate ? (
                <>
                  <span className="cg-dot" />
                  <span>
                    <strong>Готово к сборке</strong>
                    {fillableRequiredCount > 0 && (
                      <> · {filledRequiredCount} из {fillableRequiredCount} обязательных</>
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span className="cg-dot cg-dot--muted" />
                  <span>Выберите шаблон слева</span>
                </>
              )}
            </div>

            {selectedTemplate?.source_format === 'docx' && (
              <div className="cg-format-toggle" role="group" aria-label="Формат результата">
                <button
                  type="button"
                  className={`cg-format-toggle-btn ${outputFormat === 'pdf' ? 'is-active' : ''}`}
                  onClick={() => setOutputFormat('pdf')}
                  disabled={generating}
                >
                  PDF
                </button>
                <button
                  type="button"
                  className={`cg-format-toggle-btn ${outputFormat === 'docx' ? 'is-active' : ''}`}
                  onClick={() => setOutputFormat('docx')}
                  disabled={generating}
                >
                  DOCX
                </button>
              </div>
            )}

            <button
              type="button"
              className="cg-btn cg-btn--primary cg-btn--cta"
              onClick={() => void handleGenerate()}
              disabled={!selectedTemplate || generating}
            >
              <Download size={16} />
              {generating ? 'Сборка…' : ctaLabel}
            </button>
          </footer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractGeneratorModal;
